import React, { useState } from 'react';
import { arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { fetchMaterialOffers } from '../../util/api';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { getUnitTypeByCategoryId } from '../../util/categoryHelpers.js';
import { findListingFieldByKey } from '../../util/listingHelpers.js';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { getCloudinaryBaseUrl } from '../../util/cloudinary.js';
import { types as sdkTypes } from '../../util/sdkLoader';

const { UUID } = sdkTypes;

// Global ducks (for Redux actions and thunks)
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { addToCart } from '../../containers/CartPage/CartPage.duck';

// Shared components
import {
  H2,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  InlineTextButton,
  IconExport,
  OrderPanel,
  SpecConfigurator,
  LayoutSingleColumn,
  Modal,
  ReviewRating,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  requestPrice,
  sendReview,
  setInitialValues,
  requestLeadFeeProduct,
  requestQuoteFromSellers,
} from './ListingPage.duck';
import { useShareModal } from '../../components/ShareModal/ShareModal.js';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleRequestPrice,
  handleLeadFee,
  handleSubmitInquiry,
  handleSubmitRequestPrice,
  handleSubmitLeadFeeRequest,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionBreadcrumbs from './SectionBreadcrumbs.js';
import SectionGallery from './SectionGallery';
import SectionDetailsMaybe from './SectionDetailsMaybe.js';
import SectionDescriptionMaybe from './SectionDescriptionMaybe.js';
import SectionSpecsMaybe from './SectionSpecsMaybe.js';
import SectionVariantsMaybe from './SectionVariantsMaybe.js';
import SectionReviews from './SectionReviews.js';
import SectionRecentlyViewedListings from './SectionRecentlyViewedListings.js';
import SectionSimilarListings from './SectionSimilarListings.js';
import InquiryForm from './InquiryForm/InquiryForm.js';
import RequestPriceModal from './RequestPriceModal.js';

import css from './ListingPage.module.css';
import LeadFeeRequestModal from './LeadFeeRequestModal.js';
import { useEffect } from 'react';
import {
  bulkOrderCategoryForListing,
  envelopeFromReadyMix,
  offersFromListing,
  quoteProtectedData,
} from '../../util/bulkOrder';
import { fetchMaterialSellerListings } from '../../util/api';
import { createResourceLocatorString } from '../../util/routes';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [requestPriceModalOpen, setRequestPriceModalOpen] = useState(
    props.location.state?.requestPriceModalOpen
  );
  const [leadFeeRequestModalOpen, setLeadFeeRequestModalOpen] = useState(
    props.location.state?.leadFeeRequestModalOpen
  );
  const { open: onOpenShareModal, modalElement: shareModalElement } = useShareModal();

  // --- ready-mix order panel (extended flow) --------------------------------
  //
  // Ready-mix concrete is not a SKU: the catalogue row is a product FAMILY whose
  // strength / exposure / consistence are ranges the plant batches to order, so it
  // has to be specified before it can be quoted. Same product page as every other
  // material; only the order panel differs, and only for whitelisted products.
  // The panel owns its own form state; this page only holds the finished request.
  const [specOffers, setSpecOffers] = useState(null);
  const [specRequest, setSpecRequest] = useState(null);
  // Catalogue products have no seller of their own: the request goes to the seller
  // listings created from them. null = still looking, [] = nobody sells it yet.
  const [specSellers, setSpecSellers] = useState(null);


  const {
    isAuthenticated,
    currentUser,
    listingId: listingIdFromState,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    onSendReview,
    fetchReviewsInProgress,
    fetchReviewsError,
    reviews,
    recentlyViewedListings,
    fetchRecentlyViewedListingsInProgress,
    fetchRecentlyViewedListingsError,
    similarListings,
    fetchSimilarListingsInProgress,
    fetchSimilarListingsError,
    onAddToCart,
    addToCartInProgress,
    addToCartError,
    history,
    callSetInitialValues,
    sendInquiryError,
    sendInquiryInProgress,
    onSendInquiry,
    requestPriceError,
    requestPriceInProgress,
    leadFeeRequestInProgress,
    leadFeeRequestError,
    onRequestPrice: onRequestPriceFromProps,
    buyerPriceRequest,
    onRequestLeadFee,
    onRequestQuoteFromSellers,
    isSupabaseProduct: isSupabaseProductFromState,
    catalogRedirect,
    catalogProductData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
  } = props;

  const { listingFields: listingFieldConfig } = config.listing;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  // Fall back to the URL-derived listing id whenever the ListingPage duck's
  // `id` is momentarily null (e.g. right after client-side hydration when
  // loadData re-runs and dispatches setInitialValues, which wipes the id).
  // The listing entity itself is still cached in marketplaceData, so reading
  // it by the URL id avoids an unnecessary LoadingPage flash between SSR and
  // the second loadData resolution.
  const listingId =
    listingIdFromState || (rawParams.id ? new UUID(rawParams.id) : listingIdFromState);
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const deliveryMethods = ['truckDelivery', 'pickup', 'shipping'].filter(
    method => currentListing?.attributes?.publicData?.[`${method}Enabled`]
  );

  const defaultDeliveryMethod = deliveryMethods.length > 0 ? deliveryMethods[0] : null;

  const [deliveryMethod, setDeliveryMethod] = useState(defaultDeliveryMethod);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'main';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;
  const customerFullName =
    currentUser?.attributes?.profile?.firstName +
      ' ' +
      currentUser?.attributes?.profile?.lastName || '';
  const productName = currentListing?.attributes?.title;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  useEffect(() => {
    if (deliveryMethod == null && deliveryMethods.length > 0) {
      setDeliveryMethod(deliveryMethods[0]);
    }
  }, [deliveryMethods]);

  // Ready-mix order panel: fetch the offers for a candidate product.
  //
  // HOISTED ABOVE THE EARLY RETURNS BELOW, and it must stay here. Hooks have to run
  // in the same order on every render; this effect used to sit after
  // `if (!currentListing.id) return <LoadingPage/>`, so the loading render ran one
  // hook fewer than the loaded one and React threw "Rendered more hooks than during
  // the previous render". The error boundary remounted the page, which hid it —
  // but every visit to a product page threw once.
  //
  // Only categories on the bulk-order whitelist (util/bulkOrder.js) can use the
  // extended flow, so only those pay for the extra request. Since 24.09 the approval
  // is per CATEGORY: the API's `extendedFlow` is true for every product of an active
  // category sold per m³, priced by a seller or not.
  const specPublicData = currentListing?.attributes?.publicData || {};
  const specIsCatalogProduct =
    !!isSupabaseProductFromState || specPublicData.isRealListing === 'no';
  const configurableCategory = !!bulkOrderCategoryForListing(specPublicData);
  const specMaterialId = currentListing?.id?.uuid;

  useEffect(() => {
    if (!specIsCatalogProduct || !configurableCategory || !specMaterialId) {
      setSpecOffers(null);
      return undefined;
    }
    let cancelled = false;
    setSpecRequest(null);
    fetchMaterialOffers(specMaterialId)
      .then(d => {
        if (!cancelled) setSpecOffers(d);
      })
      .catch(() => {
        // Non-fatal: the page falls back to the standard catalogue panel, which is
        // what every non-whitelisted product shows anyway.
        if (!cancelled) setSpecOffers(null);
      });
    return () => {
      cancelled = true;
    };
  }, [specIsCatalogProduct, configurableCategory, specMaterialId]);

  // Who a catalogue product's quote request goes to (also hoisted — see above).
  const specExtendedFlow = !!specOffers?.extendedFlow;
  useEffect(() => {
    if (!specExtendedFlow || !specIsCatalogProduct || !specMaterialId) {
      setSpecSellers(null);
      return undefined;
    }
    let cancelled = false;
    setSpecSellers(null);
    fetchMaterialSellerListings(specMaterialId)
      .then(list => {
        if (!cancelled) setSpecSellers(list);
      })
      .catch(() => {
        if (!cancelled) setSpecSellers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [specExtendedFlow, specIsCatalogProduct, specMaterialId]);

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  // A catalogue MATERIAL id was opened but it has a published listing: go to the
  // listing's canonical URL so the full page (Request price / Contact seller)
  // renders instead of the stripped catalogue view. See ListingPage.duck.js.
  if (catalogRedirect && catalogRedirect.id && catalogRedirect.id !== params.id) {
    return (
      <NamedRedirect
        name="ListingPage"
        params={{ id: catalogRedirect.id, slug: catalogRedirect.slug || params.slug }}
        search={location.search}
      />
    );
  }

  const topbar = <TopbarContainer className={css.topbar} />;
  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;
  const { listingReviews = [], averageRating: averageListingRating = 0 } = metadata || {};

  const isSupabaseProduct = isSupabaseProductFromState || publicData?.isRealListing === 'no';

  // Variants + group specs for the variants table. The catalogue-product view carries
  // them in publicData; a seller/catalogue listing does not, so the duck fetches them
  // from the catalogue (catalogProductData) and we fall back to that. currentMaterialId
  // highlights this listing's own variant row by its catalogue material id.
  const groupData = {
    productGroup: publicData.productGroup || catalogProductData?.productGroup,
    variants: publicData.variants || catalogProductData?.variants,
    specs: publicData.specs || catalogProductData?.specs,
    aiSpecs: publicData.aiSpecs || catalogProductData?.aiSpecs,
    currentMaterialId: publicData.catalogMaterialId,
  };


  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const {
    listingType,
    transactionProcessAlias,
    unitType,
    categoryLevel3,
    categoryLevel4,
  } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';
  const unitListingField = findListingFieldByKey(listingFieldConfig, 'unit');
  const unitListingFieldOptions = unitListingField?.enumOptions || [];
  const unitListingFieldKey =
    publicData?.unit ||
    getUnitTypeByCategoryId(config.categoryUnitTypes, categoryLevel3, categoryLevel4);
  const categoryUnitType = unitListingFieldOptions.find(
    option => option.option === unitListingFieldKey
  );
  const listingBrand = findListingFieldByKey(listingFieldConfig, 'brand')?.enumOptions?.find(
    option => option.option === publicData.brand
  );

  const cloudinaryBaseUrl = getCloudinaryBaseUrl({ variantPrefix: 'square-small' });
  const listingBrandImagePath = listingBrand
    ? `${cloudinaryBaseUrl}/listingFields/brand/${listingBrand.option}.jpg`
    : '';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  // Ensure author uuid is defined before creating author profile link
  const authorId = ensuredAuthor.id?.uuid;

  const authorProfileLink = authorId && (
    <NamedLink className={css.authorDisplayName} name="ProfilePage" params={{ id: authorId }}>
      {authorDisplayName}
    </NamedLink>
  );
  const authorMessage = authorProfileLink && (
    <FormattedMessage id="ListingPage.authorLink" values={{ author: authorProfileLink }} />
  );
  const authorMessageMobile = authorProfileLink && (
    <FormattedMessage id="ListingPage.authorLinkMobile" values={{ author: authorProfileLink }} />
  );

  const authorLink = authorMessage && <span className={css.authorLink}>{authorMessage}</span>;
  const authorLinkMobile = authorMessageMobile && (
    <span className={css.authorLinkMobile}>{authorMessageMobile}</span>
  );

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  const onRequestPrice = handleRequestPrice({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setRequestPriceModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // A request sent from the ready-mix order panel carries its quantity/reference as
  // transaction data (not only in the message), so the cart can be filled with it later.
  const specQuoteData = quoteProtectedData(specRequest);

  const onSubmitRequestPrice = handleSubmitRequestPrice({
    ...commonParams,
    getListing,
    onRequestPrice: (listing, message, fullName) =>
      onRequestPriceFromProps(listing, message, fullName, specQuoteData),
    setRequestPriceModalOpen,
    customerFullName,
  });

  const onSubmitLeadFeeRequest = handleSubmitLeadFeeRequest({
    ...commonParams,
    getListing,
    onRequestLeadFee: (listing, message, method, fullName) =>
      onRequestLeadFee(listing, message, method, fullName, specQuoteData),
    setLeadFeeRequestModalOpen,
  });

  const handleLeadFeeRequest = handleLeadFee({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setLeadFeeRequestModalOpen,
  });

  const onSubmit = handleSubmit({
    ...commonParams,
    onAddToCart,
    intl,
  });

  // --- ready-mix order panel: who gets the request, and how ------------------
  //
  // A lead-fee product (lead_fee_rules: concrete per m³, sand per kg, …) goes
  // through the lead-fee request, exactly like the "Request" button of any
  // lead-fee listing; anything else through the request-price inquiry.
  //   - a SELLER listing: to that seller;
  //   - a CATALOGUE product (PDF import, no seller): to every seller listing created
  //     from it (specSellers). With none, the panel says so and sends nothing.
  const sellerBulkCategory = !isSupabaseProduct
    ? bulkOrderCategoryForListing(publicData)
    : null;
  const isSpecLeadFee = isSupabaseProduct
    ? specOffers?.isLeadFee === true || publicData.isLeadFee === true
    : publicData.isLeadFee === true;
  // A project row links here with its model quantity and specification
  // (EstimationTable → "Configure & request quote").
  const specSearch = new URLSearchParams(location.search || '');
  const specInitialQuantity = specSearch.get('quantity');
  const specInitialSpec = {
    strengthClass: specSearch.get('strength') || undefined,
    exposureClasses: (specSearch.get('exposure') || '').split(',').filter(Boolean),
    consistenceClass: specSearch.get('consistence') || undefined,
  };
  const isCatalogSpecRequest = isSupabaseProduct && !!specRequest;

  const onSpecRequestQuote = req => {
    setSpecRequest(req);
    // Through the SAME handlers the rest of the page uses, not the modal setters:
    // they send a logged-out visitor to sign-up and a pending-approval user to the
    // no-access page. Opening a modal directly did nothing at all for a visitor,
    // because the modals only render for authenticated users.
    if (isSpecLeadFee) {
      handleLeadFeeRequest();
    } else {
      onRequestPrice();
    }
  };

  const onSubmitCatalogQuote = values => {
    const listingIds = (specSellers || []).map(s => s.listingId);
    onRequestQuoteFromSellers(
      listingIds,
      values.message.trim(),
      customerFullName,
      isSpecLeadFee,
      quoteProtectedData(specRequest)
    )
      .then(txId => {
        setLeadFeeRequestModalOpen(false);
        setRequestPriceModalOpen(false);
        history.push(
          createResourceLocatorString('RequestSentPage', routeConfiguration, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  };

  const sellerCount = (specSellers || []).length;
  const catalogQuoteUnavailable =
    specSellers == null
      ? 'Checking which sellers offer this product…'
      : sellerCount === 0
      ? 'No seller offers this product on Loopbuild yet, so a quote request cannot be sent. It becomes available as soon as a seller lists it from the catalogue.'
      : null;
  const catalogRecipientNote =
    sellerCount > 0
      ? `Your request goes to ${sellerCount} seller${
          sellerCount > 1 ? 's' : ''
        } of this product. Each confirms price, plant and delivery slot.`
      : undefined;

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const handleOpenShareModal = () => {
    onOpenShareModal({
      id: 'ListingPage.shareModal',
      shareTitle: title,
      shareImage: firstImage,
    });
  };

  const firstImage = currentListing?.images?.[0];
  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const shareButton = (
    <InlineTextButton type="button" className={css.shareButton} onClick={handleOpenShareModal}>
      <IconExport className={css.shareIcon} />
    </InlineTextButton>
  );
  const titleDesktop = (
    <H3 className={css.orderPanelTitle}>
      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
      {shareButton}
    </H3>
  );

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price, intl),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapper}>
          {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
            <ActionBarMaybe
              className={css.actionBar}
              isOwnListing={isOwnListing}
              listing={currentListing}
              showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              currentUser={currentUser}
            />
          ) : null}
          {currentListing.id ? (
            <ActionBarMaybe
              className={css.actionBar}
              isOwnListing={isOwnListing}
              listing={currentListing}
              currentUser={currentUser}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
          ) : null}

          <SectionBreadcrumbs config={config} listing={currentListing} />
          <div className={css.heroColumn}>
            <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
            />
            {isSupabaseProduct ? (
              <div className={css.orderPanel}>
                <H3 className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
                {publicData.brand ? (
                  <p>{publicData.brand}</p>
                ) : null}
                {/* Whitelisted products (extendedFlow) get the ready-mix order panel
                    in place of the "contact a seller" dead end. Everything else keeps
                    the panel it had. */}
                {specOffers?.extendedFlow ? (
                  <SpecConfigurator
                    material={specOffers.material}
                    envelope={specOffers.envelope}
                    offers={specOffers.offers}
                    initialQuantity={specInitialQuantity}
                    initialSpec={specInitialSpec}
                    quoteUnavailable={catalogQuoteUnavailable}
                    recipientNote={catalogRecipientNote}
                    onRequestQuote={onSpecRequestQuote}
                  />
                ) : (
                  <p>
                    <FormattedMessage id="ListingPage.catalogProductInfo" defaultMessage="This product is available from the catalog. Contact a seller to request a quote." />
                  </p>
                )}
              </div>
            ) : sellerBulkCategory ? (
              // A seller's ready-mix listing: the same order panel, fed from the
              // seller's own data (readyMix spec, price per m³, truck delivery €/km,
              // plants) instead of the catalogue.
              <div className={css.orderPanel}>
                <H3 className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
                {authorLink}
                <SpecConfigurator
                  material={{
                    name: title,
                    brand: listingBrand?.label || publicData.brand || null,
                    standard: publicData.readyMix?.standard || null,
                  }}
                  envelope={envelopeFromReadyMix(publicData.readyMix)}
                  offers={offersFromListing(currentListing)}
                  initialQuantity={specInitialQuantity}
                  initialSpec={specInitialSpec}
                  quoteUnavailable={
                    isOwnListing ? 'This is your listing — buyers request quotes here.' : null
                  }
                  recipientNote={
                    authorDisplayName
                      ? `${authorDisplayName} confirms price, plant and delivery slot.`
                      : undefined
                  }
                  onRequestQuote={onSpecRequestQuote}
                />
              </div>
            ) : (
              <OrderPanel
                className={css.orderPanel}
                listing={currentListing}
                listingBrand={listingBrand}
                listingBrandImagePath={listingBrandImagePath}
                isOwnListing={isOwnListing}
                onSubmit={handleOrderSubmit}
                authorLink={authorLink}
                title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
                titleDesktop={titleDesktop}
                payoutDetailsWarning={payoutDetailsWarning}
                author={ensuredAuthor}
                onContactUser={onContactUser}
                onRequestPrice={onRequestPrice}
                buyerPriceRequest={buyerPriceRequest}
                onManageDisableScrolling={onManageDisableScrolling}
                validListingTypes={config.listing.listingTypes}
                categoryUnitType={categoryUnitType}
                marketplaceCurrency={config.currency}
                marketplaceName={config.marketplaceName}
                addToCartInProgress={addToCartInProgress}
                addToCartError={addToCartError}
                currentUser={currentUser}
                onSubmitLeadFeeRequest={handleLeadFeeRequest}
                deliveryMethod={deliveryMethod}
                onChangeDeliveryMethod={setDeliveryMethod}
              />
            )}
          </div>

          <div className={css.mainColumn}>
            <div className={css.mobileHeading}>
              <div className={css.orderPanelHeadingItems}>
                {listingBrand ? (
                  <span className={css.orderPanelHeadingItem}>
                    <img
                      className={css.orderPanelHeadingItemImage}
                      alt={listingBrand.label}
                      src={listingBrandImagePath}
                    />
                    {listingBrand.label}
                  </span>
                ) : null}
                <span className={css.orderPanelHeadingItem}>
                  <ReviewRating
                    reviewStarClassName={css.reviewStar}
                    rating={parseFloat(averageListingRating) || 0}
                  />
                  <FormattedMessage
                    id="ListingPage.rating"
                    values={{ count: listingReviews?.length }}
                  />
                </span>
              </div>
              <H2 className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H2>
              {authorLinkMobile}
            </div>

            <SectionDetailsMaybe
              publicData={publicData}
              metadata={metadata}
              listingFieldConfig={listingFieldConfig}
              intl={intl}
            />

            <SectionDescriptionMaybe description={description} intl={intl} />

            <SectionVariantsMaybe
              productGroup={groupData.productGroup}
              variants={groupData.variants}
              currentId={groupData.currentMaterialId || (currentListing.id && currentListing.id.uuid)}
              intl={intl}
            />

            <SectionSpecsMaybe
              specs={
                // When part of a product group, drive the spec block from the raw specs
                // (which the group's axis keys index) so the DYNAMIC specs can be removed
                // and only the STABLE ones remain. Otherwise keep the AI-summary preference.
                groupData.productGroup
                  ? groupData.specs
                  : groupData.aiSpecs && Object.keys(groupData.aiSpecs).length > 0
                  ? groupData.aiSpecs
                  : groupData.specs
              }
              excludeKeys={groupData.productGroup ? Object.keys(groupData.productGroup.axes || {}) : []}
              intl={intl}
            />

            <SectionReviews
              currentUser={currentUser}
              isOwnListing={isOwnListing}
              reviews={reviews}
              fetchReviewsInProgress={fetchReviewsInProgress}
              fetchReviewsError={fetchReviewsError}
              sendReviewInProgress={sendReviewInProgress}
              sendReviewError={sendReviewError}
              reviewSubmitted={reviewSubmitted}
              onSendReview={review => onSendReview(listingId.uuid, review)}
              intl={intl}
            />

            <SectionRecentlyViewedListings
              recentlyViewedListings={recentlyViewedListings}
              fetchRecentlyViewedListingsInProgress={fetchRecentlyViewedListingsInProgress}
              fetchRecentlyViewedListingsError={fetchRecentlyViewedListingsError}
            />
            <SectionSimilarListings
              similarListings={similarListings}
              fetchSimilarListingsInProgress={fetchSimilarListingsInProgress}
              fetchSimilarListingsError={fetchSimilarListingsError}
            />
          </div>
        </div>
        <Modal
          id="ListingPage.inquiry"
          containerClassName={css.inquiryModalContainer}
          isOpen={isAuthenticated && inquiryModalOpen}
          onClose={() => setInquiryModalOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <InquiryForm
            key={inquiryModalOpen}
            className={css.inquiryForm}
            listingTitle={title}
            authorDisplayName={authorDisplayName}
            sendInquiryError={sendInquiryError}
            onSubmit={onSubmitInquiry}
            inProgress={sendInquiryInProgress}
          />
        </Modal>
        <RequestPriceModal
          location={location}
          history={history}
          routes={routeConfiguration}
          isAuthenticated={isAuthenticated}
          author={ensuredAuthor}
          authorDisplayName={authorDisplayName}
          requestPriceInProgress={requestPriceInProgress}
          requestPriceError={requestPriceError}
          setRequestPriceModalOpen={setRequestPriceModalOpen}
          requestPriceModalOpen={requestPriceModalOpen}
          onSubmitRequestPrice={isCatalogSpecRequest ? onSubmitCatalogQuote : onSubmitRequestPrice}
          initialMessage={specRequest ? specRequest.message : undefined}
          onManageDisableScrolling={onManageDisableScrolling}
        />
        <LeadFeeRequestModal
          location={location}
          history={history}
          routes={routeConfiguration}
          isAuthenticated={isAuthenticated}
          author={ensuredAuthor}
          authorDisplayName={authorDisplayName}
          leadFeeRequestInProgress={leadFeeRequestInProgress}
          leadFeeRequestError={leadFeeRequestError}
          setLeadFeeRequestModalOpen={setLeadFeeRequestModalOpen}
          leadFeeRequestModalOpen={leadFeeRequestModalOpen}
          onSubmitLeadFeeRequest={
            isCatalogSpecRequest ? values => onSubmitCatalogQuote(values) : onSubmitLeadFeeRequest
          }
          onManageDisableScrolling={onManageDisableScrolling}
          deliveryMethod={deliveryMethod}
          customerFullName={customerFullName}
          productName={productName}
          initialMessage={specRequest ? specRequest.message : undefined}
        />
        {shareModalElement}
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  reviewSubmitted: false,
  onSendReview: null,
  fetchReviewsInProgress: false,
  fetchReviewsError: null,
  fetchRecentlyViewedListingsInProgress: false,
  fetchRecentlyViewedListingsError: null,
  fetchSimilarListingsInProgress: false,
  fetchSimilarListingsError: null,
  addToCartInProgress: false,
  addToCartError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  sendReviewInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  reviewSubmitted: bool.isRequired,
  onSendReview: func.isRequired,
  fetchReviewsInProgress: bool.isRequired,
  fetchReviewsError: propTypes.error,
  reviews: arrayOf(propTypes.listingReview),
  recentlyViewedListings: arrayOf(propTypes.listing),
  fetchRecentlyViewedListingsInProgress: bool.isRequired,
  fetchRecentlyViewedListingsError: propTypes.error,
  similarListings: arrayOf(propTypes.listing),
  fetchSimilarListingsInProgress: bool.isRequired,
  fetchSimilarListingsError: propTypes.error,
  onAddToCart: func.isRequired,
  addToCartInProgress: bool.isRequired,
  addToCartError: propTypes.error,
  callSetInitialValues: func.isRequired,
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    id: listingId,
    showListingError,
    sendInquiryInProgress,
    sendInquiryError,
    requestPriceInProgress,
    requestPriceError,
    leadFeeRequestInProgress,
    leadFeeRequestError,
    inquiryModalOpenForListingId,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    fetchReviewsInProgress,
    fetchReviewsError,
    reviewIds,
    recentlyViewedListingIds,
    fetchRecentlyViewedListingsInProgress,
    fetchRecentlyViewedListingsError,
    similarListingIds,
    fetchSimilarListingsInProgress,
    fetchSimilarListingsError,
    isSupabaseProduct,
    catalogRedirect,
    catalogProductData,
    buyerPriceRequest,
  } = state.ListingPage;
  const { addToCartInProgress, addToCartError } = state.CartPage;

  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const reviews = getMarketplaceEntities(state, reviewIds);
  const recentlyViewedListings = getListingsById(state, recentlyViewedListingIds);
  const similarListings = getListingsById(state, similarListingIds);

  return {
    isAuthenticated,
    currentUser,
    listingId,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    sendInquiryInProgress,
    sendInquiryError,
    requestPriceInProgress,
    requestPriceError,
    leadFeeRequestInProgress,
    leadFeeRequestError,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    fetchReviewsInProgress,
    fetchReviewsError,
    reviews,
    recentlyViewedListings,
    fetchRecentlyViewedListingsInProgress,
    fetchRecentlyViewedListingsError,
    similarListings,
    fetchSimilarListingsInProgress,
    fetchSimilarListingsError,
    isSupabaseProduct,
    catalogRedirect,
    catalogProductData,
    buyerPriceRequest,
    addToCartInProgress,
    addToCartError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onRequestPrice: (listing, message, customerFullName, quoteData) =>
    dispatch(requestPrice(listing, message, customerFullName, quoteData)),
  onSendReview: (listingId, review) => dispatch(sendReview(listingId, review)),
  onAddToCart: (listingId, quantity, deliveryMethod) =>
    dispatch(addToCart(listingId, quantity, deliveryMethod)),
  onRequestLeadFee: (listing, message, deliveryMethod, customerFullName, quoteData) =>
    dispatch(requestLeadFeeProduct(listing, message, deliveryMethod, customerFullName, quoteData)),
  onRequestQuoteFromSellers: (listingIds, message, customerFullName, isLeadFee, quoteData) =>
    dispatch(
      requestQuoteFromSellers(listingIds, message, customerFullName, isLeadFee, quoteData)
    ),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
